/**
 * Commerce › Vente › Affaires : règles pures partagées par l'API, le mode maquette et le front (cadrage :
 * docs/COMMERCE-CADRAGE.md §3-4). Une AFFAIRE est le projet d'un client final (un méthaniseur, un site) ; elle a :
 *  - des SOURCES (qui l'a apportée : un acteur – tiers et rôle – ou un canal sans tiers), dont une principale ;
 *  - des VOIES DE VENTE (par qui elle peut être signée : client direct, intégrateur…), dont au plus une porteuse,
 *    seule comptée dans le pipeline ;
 *  - des ACTEURS (tout tiers impliqué, avec son rôle) et des BESOINS par gamme.
 * Valeurs des listes : mêmes valeurs que la migration commerce/20261026_1000 (test de cohérence api/test).
 */
import { ROLES_TIERS } from '../schemas';

export const RESSOURCE_AFFAIRES = 'commerce.vente.affaires';

/** Étapes du processus G0 « Vendre » (Ghislain) : GET indicatif (%) et délai de relance (jours) – provisoires. */
export const ETAPES_AFFAIRE = [
  { code: 'sollicitation', get: 1, relance: 3 },
  { code: 'qualification', get: 10, relance: 7 },
  { code: 'offre', get: 30, relance: 15 },
  { code: 'negociation', get: 40, relance: 10 },
  { code: 'phase-finale', get: 85, relance: 7 },
  { code: 'comite', get: 85, relance: 5 },
  { code: 'signe-sous-reserves', get: 100, relance: 30 },
  { code: 'reserves-levees', get: 100, relance: 10 },
  { code: 'kom-interne', get: 100, relance: 10 },
  { code: 'kom-client', get: 100, relance: 15 },
] as const;
export type EtapeAffaire = (typeof ETAPES_AFFAIRE)[number]['code'];
export const CODES_ETAPE_AFFAIRE = ETAPES_AFFAIRE.map((e) => e.code) as [EtapeAffaire, ...EtapeAffaire[]];

/** Gagnée à la signature (étape 7, D11) ; perdue, abandonnée, non retenue (No-go) : clôturées. */
export const STATUTS_AFFAIRE = ['en-cours', 'gagnee', 'perdue', 'abandonnee', 'non-retenue'] as const;
/** D3 : visibilité de la filiale ou restreinte à des groupes (pas d'affaire personnelle). */
export const VISIBILITES_AFFAIRE = ['filiale', 'restreinte'] as const;

/** Source : un acteur (tiers + rôle dans le projet) ou un canal sans tiers. */
export const NATURES_SOURCE = ['acteur', 'canal'] as const;
export const ROLES_SOURCE = ROLES_TIERS;
export const CANAUX_SOURCE = ['salon', 'site-web', 'prospection', 'collaborateur', 'appel-offres', 'autre'] as const;

/** Voie de vente : par qui l'affaire peut être signée. */
export const TYPES_VOIE = ['client-direct', 'integrateur', 'prescripteur', 'bureau-etudes'] as const;
export const STATUTS_VOIE = ['en-cours', 'gagnee', 'perdue', 'abandonnee'] as const;

/** Gammes des besoins (lignes de besoin, puis devis de référence au lot 2). */
export const GAMMES_BESOIN = ['valopur', 'valogaz', 'valopack', 'valotherm', 'service', 'autre'] as const;

/**
 * Données techniques du site (affaire) reprises dans le besoin technique de chaque nouveau devis : pression
 * d'injection, pureté CH₄ visée, composition du biogaz (schemaBesoinTechniqueAffaire ; le commentaire reste sur l'affaire).
 */
export const CHAMPS_TECHNIQUES_SITE = ['pressionBar', 'pureteCh4', 'ch4Pct', 'co2Pct', 'h2sPpm', 'o2Pct'] as const;

/** Objet sans ses valeurs non renseignées (null, undefined) : données techniques du site, besoin d'un devis. */
export const valeursRenseignees = (o: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== undefined));

/** Concurrents d'une affaire : en lice, écarté, retenu par le client (au plus un). */
export const STATUTS_CONCURRENT = ['en-lice', 'ecarte', 'retenu'] as const;
export const RESSOURCE_CONCURRENTS = 'commerce.prospection.concurrents';

/** Référence lisible : AFF-FR01-2026-0001 (numéro par filiale et par année, comme les incidents IT). */
export const referenceAffaire = (filiale: string, annee: number, numero: number) =>
  `AFF-${filiale}-${annee}-${String(numero).padStart(4, '0')}`;

/**
 * Montant pondéré (pipeline) = montant de la voie porteuse × GO × GET de la voie porteuse ; null s'il manque une
 * donnée (pas de voie porteuse, montant ou probabilité non renseignés).
 */
export function montantPondere(a: { go: number | null; voies: { porteuse: boolean; montant: number | null; get: number | null }[] }): number | null {
  const p = a.voies.find((v) => v.porteuse);
  if (!p || p.montant === null || p.get === null || a.go === null) return null;
  return Math.round(p.montant * (a.go / 100) * (p.get / 100) * 100) / 100;
}
